import { FC, useCallback, useRef, useState } from 'react';
import Page, { PageProps } from '../../components/page';
import { Box } from '@mui/material';
import DataTable, { DataTableInstance } from '../../components/data-table';
import PageHeader from '../../components/page/header';
import PageBlurb from '../../components/page/blurb';
import useDrawer from '@/hooks/use-drawer.hook';
import { PartnerRoleLabels } from '../../lib/models/partner-user.model';
import Text from '../../components/text';
import Button from '../../components/button';
import { DtColumn, DtFilter, DtSort } from '@/components/data-table/helpers';
import { getPartnerUsers, isPartnerUserDomainInvalid } from '@/lib/services/partner-user.service';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import DomSize from '@/components/dom-size';
import { getPartner } from '@/lib/services/partner.service';
import Chip from '@/components/chip';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'email', label: 'Email', flex: 3, sortable: true },
  { name: 'role', label: 'Role', sortable: true },
  { name: 'action', label: '', sortable: false },
];

const PartnersUsersPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');

  const dtRef = useRef<DataTableInstance>(null);

  const handleRefresh = () => {
    dtRef.current?.refresh();
  };

  const handleOpenDrawer = useCallback(
    (id?: string | null) => {
      openDrawer('partner-user', {
        id: id || null,
        onChange: handleRefresh,
        onClose: () => setSelectedRowId(''),
      });

      if (id) {
        setSelectedRowId(id);
      }
    },
    [openDrawer]
  );

  const loadData = useCallback(
    async (page: number, pageSize: number, sort?: DtSort, filter?: DtFilter) => {
      const [dataset, partner] = await Promise.all([
        getPartnerUsers(page, pageSize, sort, filter),
        getPartner(),
      ]);

      return {
        ...dataset,
        rows: dataset.rows.map((partnerUser) => {
          const { id, name, email, role } = partnerUser;
          const domainInvalid = isPartnerUserDomainInvalid(partnerUser, partner || null);

          return {
            id,
            name: <Box maxWidth="100%">{name}</Box>,
            email: (
              <Box display="flex" alignItems="center" gap={1}>
                {email}
                {domainInvalid && <Chip label="Invalid Domain" color="error" size="small" />}
              </Box>
            ),
            role: <Text size="small">{PartnerRoleLabels[role]}</Text>,
            action: (
              <Box width="100%" display="flex" justifyContent="flex-end">
                <OpenDrawerButton onClick={() => handleOpenDrawer(id)} />
              </Box>
            ),
          };
        }),
      };
    },
    [handleOpenDrawer]
  );

  return (
    <Page title="Partner Admins">
      <PageHeader>
        <PageBlurb>
          Create new partner admin, and view and change permissions for existing partner admins.
        </PageBlurb>
        <Button
          label="Add Partner Admin"
          icon="plus"
          size="small"
          onClick={() => handleOpenDrawer()}
        />
      </PageHeader>

      <DomSize>
        <DataTable
          title=""
          queryKey="partner-users"
          columns={columns}
          onLoad={loadData}
          ref={dtRef}
          search={true}
          sort={{ columnName: 'name', direction: 'asc' }}
          selectedRowId={selectedRowId}
        />
      </DomSize>
    </Page>
  );
};

export default PartnersUsersPage;
